import { computed, toValue } from 'vue'
import type { MaybeRefOrGetter } from 'vue'
import { useUxData } from '@/stores/ux-data'
import type { SwadeAdditionalDataInterface } from '@/types/characters'

/**
 * A character's archetype — `additionalData.archetype` — and its label, for every surface that
 * shows or edits it: the sheet's Details tab, the guided setup's first step and its concept panel.
 *
 * A sheet may have no `additionalData` yet: the first write creates it. A blank value is written as
 * null, never as a blank string.
 */
export function useCharacterArchetype(
  character: MaybeRefOrGetter<{ additionalData?: SwadeAdditionalDataInterface } | null | undefined>,
) {
  const ux = useUxData()

  const archetypeLabel = computed(() => ux.labels?.characterCreator?.archetype ?? 'Archetype')

  const archetype = computed({
    get: (): string | null | undefined => toValue(character)?.additionalData?.archetype,
    set: (value: string) => {
      const sheet = toValue(character)
      if (!sheet) return

      sheet.additionalData ??= {}
      sheet.additionalData.archetype = value.trim() === '' ? null : value
    },
  })

  return { archetype, archetypeLabel }
}
